import type React from "react"
import useBreakpoint from "../../hooks/useBreakpoint"
import Modal, { type ModalPropsType } from "../Modal"
import { DesktopPopover } from "./DesktopPopover"
import type {
  DesktopPopoverCoreRefs,
  PopoverConfig,
} from "./useDesktopPopoverCore"

type DesktopPopoverRenderProps = {
  refs: DesktopPopoverCoreRefs
  getReferenceProps: ReturnType<
    typeof import("@floating-ui/react").useInteractions
  >["getReferenceProps"]
  renderFloating: (content: React.ReactNode) => React.ReactNode
}

export type DesktopPopoverChildren =
  | React.ReactNode
  | ((props: DesktopPopoverRenderProps) => React.ReactNode)

export type PopoverPropsType = PopoverConfig & {
  children?: DesktopPopoverChildren
  isOpen: boolean
  onOpenChange?: (open: boolean) => void
  "aria-label"?: string
  targetRef: React.RefObject<Element | null> | Element | null
  responsive?: boolean
}

const MobilePopover = ({
  isOpen,
  children,
  ariaLabel = "Popover mobile modal",
  onDismiss,
  bodySpacing,
  onShow,
  onHidden,
}: {
  children?: React.ReactNode
  ariaLabel?: ModalPropsType["aria-label"]
  isOpen: ModalPropsType["isOpen"]
  onDismiss?: () => void
  bodySpacing: ModalPropsType["bodySpacing"]
  onShow: ModalPropsType["onShow"]
  onHidden: ModalPropsType["onHidden"]
}) => {
  return (
    <Modal
      aria-label={ariaLabel}
      isOpen={isOpen}
      onDismissAttempt={onDismiss}
      bodySpacing={bodySpacing}
      onShow={onShow}
      onHidden={onHidden}
    >
      {children}
    </Modal>
  )
}

const Popover = ({
  arrow = false,
  placement = "bottom-start",
  bodySpacing = true,
  appendToBody = false,
  fitContent = false,
  responsive = true,
  ...restProps
}: PopoverPropsType) => {
  const { breakpoint } = useBreakpoint()
  const isMobile = responsive && (breakpoint === "sm" || breakpoint === "xs")

  const mobileChildren =
    typeof restProps.children === "function" ? null : restProps.children

  return isMobile ? (
    <MobilePopover
      isOpen={restProps.isOpen}
      onDismiss={() => {
        restProps.onOpenChange?.(false)
      }}
      bodySpacing={bodySpacing}
      ariaLabel={restProps["aria-label"]}
      onShow={restProps.onShow}
      onHidden={restProps.onHidden}
    >
      {mobileChildren}
    </MobilePopover>
  ) : (
    <DesktopPopover
      arrow={arrow}
      placement={placement}
      bodySpacing={bodySpacing}
      appendToBody={appendToBody}
      fitContent={fitContent}
      {...restProps}
    />
  )
}

export default Popover
